import { ReactNode, useContext, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@dealstack/frontend/shared/components/DashboardLayout/DashboardLayout';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DealSettingsSidebar } from '@dealstack/frontend/app/pages/Project/DealSettings/DealSettingsSidebar';
import { DealGeneralSettings } from '@dealstack/frontend/app/pages/Project/DealSettings/DealGeneralSettings/DealGeneralSettings';
import { DealUsersSettings } from '@dealstack/frontend/app/pages/Project/DealSettings/DealUsersSettings/DealUsersSettings';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import { ClassifierSettings } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/Classifiers';
import { DealEmailTemplates } from '@dealstack/frontend/app/pages/Project/DealSettings/EmailTemplates/DealEmailTemplates';
import { DealEmailTemplateDetailPage } from '@dealstack/frontend/app/pages/Project/DealSettings/EmailTemplates/DealEmailTemplateDetailPage';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { UserManagement } from '@dealstack/frontend/shared/components/UserManagement/UserManagement';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	addDealPermissionToUser,
	getUserForDeal,
	removeDealPermissionFromUser,
	syncUserInDealToAllProgrammes,
} from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.api';
import { useAppDispatch, useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { selectSelectedDealTeamUserId } from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.selectors';
import { setSelectedDealTeamUserId } from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.slice';
import { DealSettingsView } from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.types';
import {
	getResourceTypePermissionsWithOwner,
	PermissionRole,
	PermissionWithOwner,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { getDealUsers } from '@dealstack/frontend/shared/dataExchange/services/project.service';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { transformPermissions } from '@dealstack/frontend/shared/components/UserPermissions/userPermissions.utils';
import { setPermissionsForResourceType } from '@dealstack/frontend/shared/dataExchange/services/permission.api';

import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { useDeleteDealUser } from '@dealstack/frontend/app/pages/Project/DealDetails/hooks';
import { generatePath, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
	DEAL_ROUTES,
	DEAL_SETTINGS_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { DealDropdown } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealDropdown/DealDropdown.tsx';

interface Props {
	children: ReactNode;
}

/**
 * The member page is addressable now, so the id in the URL is what selects the member: a refresh or
 * a pasted link arrives with nothing in the store, and the user query would stay disabled behind it.
 */
const SelectedDealUserFromRoute = ({ children }: Props) => {
	const dispatch = useAppDispatch();
	const { userId = '' } = useParams<{ userId: string }>();
	const selectedDealTeamUserId = useAppSelector(selectSelectedDealTeamUserId);

	useEffect(() => {
		if (userId && userId !== selectedDealTeamUserId) {
			dispatch(setSelectedDealTeamUserId(userId));
		}
	}, [dispatch, selectedDealTeamUserId, userId]);

	return <>{children}</>;
};

export const DealSettings = () => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { deals, deal, dealId } = useContext(DealContext);
	const tCommonUi = useTranslationCommonUi();
	const selectedDealTeamUserId = useAppSelector(selectSelectedDealTeamUserId);
	const currentUserId = useAppSelector(selectUserId);
	const navigate = useNavigate();
	// Views are addressable, as they are in programme settings: the sidebar navigates, and an open
	// template is a URL of its own rather than a piece of component state.
	const { '*': currentPath } = useParams();

	const currentView = useMemo(() => {
		if (!currentPath) {
			return DealSettingsView.General;
		}

		if (currentPath.startsWith(`${DealSettingsView.User}/`)) {
			return DealSettingsView.User;
		}

		return (
			Object.values(DealSettingsView).find(
				(view) => view !== DealSettingsView.General && currentPath.startsWith(view)
			) ?? DealSettingsView.General
		);
	}, [currentPath]);

	const settingsPath = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId });
	const goToView = (view: DealSettingsView) =>
		navigate(view === DealSettingsView.General ? settingsPath : `${settingsPath}/${view}`);
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const breadcrumbs = useMemo(() => {
		if (inPageFeatureFlag) {
			return [
				{
					name: deal?.name ?? 'Deal',
					url: generatePath(ROUTES.DEAL, { dealId }),
					element: (
						<DealDropdown
							key="deal-dropdown"
							currentDealId={dealId}
							deals={deals ?? []}
						/>
					),
				},
			];
		} else {
			return [
				...makeDealPageBreadcrumbs({
					tCommonUi,
					dealId,
					dealName: deal?.name,
					deals: deals ?? [],
				}),
				{
					name: tCommonUi('breadcrumbs.deal_settings'),
					url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId }),
				},
			];
		}
	}, [deal?.name, dealId, deals, inPageFeatureFlag, tCommonUi]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	const { refetch: refetchDealTeamUsers } = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
				dealId: dealId!,
			}),
		],
		queryFn: () =>
			getDealUsers(
				formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
					dealId: dealId!,
				})
			),
		enabled: false,
	});

	const { mutate: deleteDealUser, isPending: isDeletingDealUser } = useDeleteDealUser();
	const removeUser = (userId: string) => {
		deleteDealUser(
			{
				dealId: dealId!,
				userId,
			},
			{
				onSuccess: async () => {
					showToastV2({
						message: tAdminDashboard(
							'deal_settings.team.toast.remove_user.success_message'
						),
					});
					navigate(`${settingsPath}/${DealSettingsView.Members}`);
					await refetchDealTeamUsers();
				},
			}
		);
	};

	const {
		data: user,
		isFetching: isFetchingUser,
		refetch: refetchUser,
	} = useQuery({
		queryKey: [API_PATHS.DEAL_DEALID_USERS_USERID_GET, { id: selectedDealTeamUserId, dealId }],
		queryFn: () => getUserForDeal(selectedDealTeamUserId!, dealId!),
		enabled: !!selectedDealTeamUserId && !!dealId,
		select: (data) => {
			const dealPermissions = getResourceTypePermissionsWithOwner(ResourceType.DEAL);
			const resourcePermissions = dealPermissions.map((permission) => {
				return {
					label: tAdminDashboard(`invite_team_member.relation.${permission}`),
					value: permission,
					description: tAdminDashboard(
						`invite_team_member.relation.description.${permission}`
					),
				};
			});
			const isCurrentUser = currentUserId === selectedDealTeamUserId;
			const permissions = transformPermissions({
				resourcePermissions,
				userPermissions: data.payload.permissions as unknown as PermissionWithOwner[],
				disablePermissionForCurrentUser: isCurrentUser,
				resourceType: ResourceType.DEAL,
			});
			return {
				...data.payload,
				permissions,
			};
		},
	});

	const { mutate: syncUserInDealToAllProgrammesMutation } = useMutation({
		mutationFn: syncUserInDealToAllProgrammes,
	});

	const { mutate: addDealPermissionToUserMutation, isPending: isAddingDealPermissionToUser } =
		useMutation({
			mutationFn: addDealPermissionToUser,
		});
	const { mutate: setDealPermissionToUserMutation, isPending: isSetDealPermissionToUser } =
		useMutation({
			mutationFn: (variables: Parameters<typeof setPermissionsForResourceType>[0]) =>
				setPermissionsForResourceType(variables),
		});
	const {
		mutate: removeDealPermissionFromUserMutation,
		isPending: isRemovingDealPermissionFromUser,
	} = useMutation({
		mutationFn: removeDealPermissionFromUser,
	});

	const updatePermission = (permissionRole: PermissionRole, removePermission: boolean) => {
		const explicitPermissions = user?.permissions
			// Must check for false and not undefined
			.filter((p) => p.isImplicit === false)
			.map((p) => p.value);

		const onSuccess = async () => {
			showToastV2({
				message: tAdminDashboard('toast.update_user_permission.success_message'),
			});

			await refetchUser();
			await refetchDealTeamUsers();
			syncUserInDealToAllProgrammesMutation({
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
				userId: user?.id!,
				dealId: dealId!,
			});
		};

		// Removing the last explicit Permission
		if (explicitPermissions?.length === 1 && removePermission) {
			return setDealPermissionToUserMutation(
				{
					body: {
						userId: selectedDealTeamUserId!,
						permissionRoles: [PermissionRole.VIEWER],
					},
					params: { resourceType: ResourceType.DEAL, resourceId: dealId! },
				},
				{ onSuccess }
			);
		}
		if (removePermission) {
			return removeDealPermissionFromUserMutation(
				{
					dealId: dealId!,
					userId: selectedDealTeamUserId!,
					permissionRoles: [permissionRole],
				},
				{ onSuccess }
			);
		}

		return addDealPermissionToUserMutation(
			{
				dealId: dealId!,
				userId: selectedDealTeamUserId!,
				permissionRoles: [permissionRole],
			},
			{ onSuccess }
		);
	};

	const pageTitle =
		currentView === DealSettingsView.EmailTemplates
			? tAdminDashboard('deal_settings.labels.email_templates', {
					defaultValue: 'Email templates',
				})
			: tAdminDashboard('deal_settings.title');

	const selectUserAndView = (_view: DealSettingsView, userId: string) =>
		navigate(`${settingsPath}/${DealSettingsView.User}/${userId}`);

	const userManagement = (
		<UserManagement
			title={deal?.name || ''}
			deleteFromText={tCommonUi('title.deal')}
			user={user}
			membersBreadcrumbTitle={tAdminDashboard('deal_settings.labels.deal_team')}
			isLoading={
				isFetchingUser ||
				isRemovingDealPermissionFromUser ||
				isAddingDealPermissionToUser ||
				isSetDealPermissionToUser ||
				isDeletingDealUser
			}
			// TODO: Will implement this functionality in the future
			handleUpdateUser={() => Promise.resolve()}
			handlePermissionChange={updatePermission}
			handleRemoveUser={removeUser}
			canRemoveUser={true}
			text={{
				permissionTitle: tAdminDashboard('invite_member_drawer.form.role_field.label'),
			}}
		/>
	);

	// Every view keeps the settings frame — sidebar, page title — except an open email template,
	// which takes the whole page the way a programme template does.
	const withSettingsFrame = (content: ReactNode) => (
		<DashboardLayout
			title={pageTitle}
			showBreadcrumbs={true}
			sidebar={
				<DealSettingsSidebar
					title={deal?.name || ''}
					currentView={currentView}
					onViewChange={goToView}
				/>
			}
		>
			{content}
		</DashboardLayout>
	);

	return (
		<BreadcrumbsProvider>
			<Routes>
				<Route
					index
					element={withSettingsFrame(<DealGeneralSettings onViewChange={goToView} />)}
				/>
				<Route
					path={DEAL_SETTINGS_ROUTES.MEMBERS}
					element={withSettingsFrame(
						<DealUsersSettings onViewChange={selectUserAndView} />
					)}
				/>
				<Route
					path={DEAL_SETTINGS_ROUTES.CLASSIFIERS}
					element={withSettingsFrame(<ClassifierSettings />)}
				/>
				<Route
					path={DEAL_SETTINGS_ROUTES.USER}
					element={withSettingsFrame(
						<SelectedDealUserFromRoute>{userManagement}</SelectedDealUserFromRoute>
					)}
				/>
				<Route
					path={DEAL_SETTINGS_ROUTES.EMAIL_TEMPLATES}
					element={withSettingsFrame(<DealEmailTemplates dealId={dealId!} />)}
				/>
				<Route
					path={DEAL_SETTINGS_ROUTES.EMAIL_TEMPLATE_DETAIL}
					element={<DealEmailTemplateDetailPage dealId={dealId!} />}
				/>
				<Route
					path="*"
					element={withSettingsFrame(<DealGeneralSettings onViewChange={goToView} />)}
				/>
			</Routes>
		</BreadcrumbsProvider>
	);
};
